import { Button, Grid, Paper } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getWebFormByName, updateWebForm } from 'src/shared/services/webFormService';
import * as yup from 'yup';
import { Schema } from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { WebFormEmbedCode } from '../components/webFormEmbedCode';
import { WebFormSettingsOverview } from '../components/webFormSettings';
import { WebForm } from '../models/webForm';

interface PartialWebFormEditable {
  subjectSender: string;
  welcomeURL: string;
  bodySender: string;
}

export const WebFormsDetailPage = () => {
  const params = useParams();

  const isMounted = useIsMounted();
  const { t } = useTranslation();

  const [loading, setLoading] = useState<boolean>(false);
  const [webForm, setWebform] = useState<WebForm>();
  const [edit, setEdit] = useState<boolean>(false);

  const requiredMessage = t('common:validationMessages.required');

  // const convertLineBreaksToHtml = (text: string) => {
  //   return text.replace(/(\r\n|\n|\r)/g, '<br />');
  // };

  const webFormEditableSchema: Schema<PartialWebFormEditable> = yup.object().shape({
    subjectSender: yup.string().required(requiredMessage),
    welcomeURL: yup.string().required(requiredMessage),
    bodySender: yup.string().required(requiredMessage),
  });

  const formMethods = useForm<PartialWebFormEditable>({
    resolver: yupResolver(webFormEditableSchema),
    mode: 'all',
    defaultValues: {
      subjectSender: '',
      welcomeURL: '',
      bodySender: '',
    },
  });

  const { handleSubmit, reset } = formMethods;

  const fetchData = useCallback(async () => {
    setLoading(true);
    if (params.name) {
      try {
        const result = await getWebFormByName(params.name);
        if (isMounted()) {
          setWebform(result.data);
          reset({
            subjectSender: result.data.settings.find((s) => s.keyName === 'subjectSender')?.varchar || '',
            welcomeURL: result.data.settings.find((s) => s.keyName === 'welcomeURL')?.varchar || '',
            bodySender: result.data.settings.find((s) => s.keyName === 'bodySender')?.text || '',
          });
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.error(e);
      } finally {
        if (isMounted()) setLoading(false);
      }
    }
  }, [isMounted, params.name, reset]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  const onSubmit = useCallback(
    async (data: PartialWebFormEditable) => {
      if (!webForm) return;

      const updatedSettings = webForm.settings.map((s) => {
        switch (s.keyName) {
          case 'subjectSender':
            return { ...s, varchar: data.subjectSender };
          case 'welcomeURL':
            return { ...s, varchar: data.welcomeURL };
          case 'bodySender':
            return { ...s, text: data.bodySender };
          default:
            return s;
        }
      });

      const updatedWebForm: WebForm = {
        ...webForm,
        settings: updatedSettings,
      };

      try {
        setLoading(true);
        ConsoleHelper.log('Updating fundraiser settings:', updatedWebForm);
        await updateWebForm(updatedWebForm);
        await fetchData();

        setEdit(false);
      } catch (error) {
        ConsoleHelper.log(error);
      } finally {
        setLoading(false);
      }
    },
    [fetchData, webForm],
  );

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormLoaderWrapper loading={loading}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <MainContentHeader title={webForm?.formDescription ?? ''} subtitle={webForm?.formName} />
            </Grid>
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Grid spacing={3} container>
                  {webForm && (
                    <>
                      <WebFormEmbedCode webFormName={webForm.formName} />
                      <WebFormSettingsOverview isEditable={edit} />
                    </>
                  )}
                </Grid>
                <Grid item xs={12} mt={3} display='flex' justifyContent='flex-end'>
                  {edit && (
                    <Button size='medium' variant='contained' sx={{ mr: 2, mb: 2 }} disabled={loading} type='submit'>
                      {t('common:button.save')}
                    </Button>
                  )}
                  {!edit && (
                    <Button size='medium' variant='outlined' sx={{ mr: 2, mb: 2 }} disabled={loading || !webForm} onClick={() => setEdit(true)}>
                      {t('common:button.edit')}
                    </Button>
                  )}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </FormLoaderWrapper>
      </form>
    </FormProvider>
  );
};
